import { useRef, useState } from "react";

import { apiErrorMessage } from "../../lib/api";
import {
  CLIENT_STATUS_HINT,
  CLIENT_STATUS_LABEL,
  VERIFY_CHECK_LABEL,
  useClients,
  useCreateClient,
  useDeleteClient,
  useKbIdentity,
  useProvisionClient,
  useSaveKbIdentity,
  useUpdateClient,
  useVerifyClient,
  type AdminClient,
  type ClientStatus,
} from "../../lib/adminApi";
import { useIsSuperAdmin } from "../../lib/kbApi";

/**
 * SuperAdminPage — where a new customer company is set up.
 *
 * The point of the module: adding the second customer should be data entry, not an
 * engineering project. Three things happen here, in order, and the UI keeps them in
 * that order because doing them out of order silently produces a customer who
 * cannot log in:
 *
 *   1. Register the company (slug, name, email domains) — nothing external yet.
 *   2. Configure the access — creates the Auth0 Organization and invites the first
 *      administrator. This is the step that costs real money and sends a real email.
 *   3. Give its assistant an identity — writes to the Knowledge Base service, which
 *      addresses tenants by Auth0 Organization, so it is only possible after 2.
 *
 * Written for a non-developer: every status carries a sentence saying what it means
 * for the customer, and "verify" reports what Auth0 ACTUALLY holds rather than what
 * our own row claims. That last distinction is not academic — a page in this
 * project's wiki asserted for weeks that two Auth0 roles existed when neither did.
 */
export function SuperAdminPage(): JSX.Element {
  const isSuperAdmin = useIsSuperAdmin();
  const clients = useClients(isSuperAdmin);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!isSuperAdmin) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="text-xl font-semibold text-ink">Non disponibile</h1>
        <p className="mt-2 text-sm text-ink/70">
          Questa sezione è riservata all&apos;amministrazione della piattaforma.
        </p>
      </main>
    );
  }

  const selected = clients.data?.find((c) => c.id === selectedId) ?? null;

  return (
    <main className="mx-auto max-w-5xl px-6 py-8">
      <h1 className="text-xl font-semibold text-ink">Clienti della piattaforma</h1>
      <p className="mt-1 text-sm text-ink/70">
        Registra una nuova azienda cliente, abilitane l&apos;accesso e configura il suo assistente.
      </p>

      <CreateClientForm />

      <section className="mt-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink/60">Clienti</h2>
        {clients.isLoading ? (
          <p className="mt-3 text-sm text-ink/60">Caricamento…</p>
        ) : clients.isError ? (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {apiErrorMessage(clients.error)}
          </p>
        ) : !clients.data?.length ? (
          <p className="mt-3 text-sm text-ink/60">
            Nessun cliente registrato. Compila il modulo sopra per iniziare.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-line rounded-lg border border-line bg-white">
            {clients.data.map((client) => (
              <li key={client.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-medium text-ink">{client.displayName}</p>
                    <p className="font-mono text-xs text-ink/60">{client.slug}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <StatusPill status={client.status} />
                    <button
                      type="button"
                      onClick={() => setSelectedId((id) => (id === client.id ? null : client.id))}
                      className="text-sm text-brand underline hover:no-underline"
                    >
                      {selectedId === client.id ? "Chiudi" : "Gestisci"}
                    </button>
                  </div>
                </div>
                {selected?.id === client.id ? <ClientDetail client={selected} /> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

/**
 * A synchronous "already submitting" latch.
 *
 * `mutation.isPending` is React state, so between `mutate()` and the re-render that
 * flips it a second submit still reads `false`. A `disabled` attribute has the same
 * lag, and Enter inside a form ignores it anyway. With a TanStack `scope` the extra
 * call is not even dropped — it is queued and runs once the first settles.
 *
 * Returns a `claim()` that yields true at most once until `release()` is called.
 */
function useSubmitLatch(): { claim: () => boolean; release: () => void } {
  const busy = useRef(false);
  return {
    claim: () => {
      if (busy.current) return false;
      busy.current = true;
      return true;
    },
    release: () => {
      busy.current = false;
    },
  };
}

const STATUS_TONE: Record<ClientStatus, string> = {
  draft: "bg-ink/10 text-ink/70",
  provisioning: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  failed: "bg-red-100 text-red-700",
  suspended: "bg-ink/10 text-ink/60",
};

function StatusPill({ status }: { status: ClientStatus }): JSX.Element {
  return (
    <span
      // The label alone ("Bozza") does not tell a non-technical reader that nobody
      // can log in yet, so the meaning travels with it.
      title={CLIENT_STATUS_HINT[status]}
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[status]}`}
    >
      {CLIENT_STATUS_LABEL[status]}
    </span>
  );
}

function CreateClientForm(): JSX.Element {
  const create = useCreateClient();
  const latch = useSubmitLatch();
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");

  function onSubmit(event: React.FormEvent): void {
    event.preventDefault();
    if (!latch.claim()) return;
    create.mutate(
      {
        slug: slug.trim(),
        displayName: displayName.trim(),
      },
      {
        onSuccess: () => {
          setSlug("");
          setDisplayName("");
        },
        onSettled: latch.release,
      },
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-6 rounded-lg border border-line bg-white p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink/60">
        1 · Registra un cliente
      </h2>
      <p className="mt-1 text-xs text-ink/60">
        Solo dati: Auth0 non viene toccato in questo passaggio.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-ink">Nome azienda</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            placeholder="Revo S.p.A."
            className="mt-1 w-full rounded border border-line px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ink">Identificativo</span>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            placeholder="revo-spa"
            className="mt-1 w-full rounded border border-line px-3 py-2 font-mono text-sm"
          />
          {/* Auth0's own rule, stated up front rather than discovered as a 422:
              the slug becomes the Organization name and cannot be changed later. */}
          <span className="mt-1 block text-xs text-ink/60">
            Minuscole, numeri, «-» e «_». Non modificabile dopo la creazione.
          </span>
        </label>
      </div>
      <button
        type="submit"
        disabled={create.isPending}
        className="mt-4 rounded bg-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
      >
        {create.isPending ? "Creazione…" : "Registra"}
      </button>
      {create.isError ? (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {apiErrorMessage(create.error)}
        </p>
      ) : null}
    </form>
  );
}

function ClientDetail({ client }: { client: AdminClient }): JSX.Element {
  return (
    <div className="mt-4 space-y-5 rounded border border-line bg-ink/[0.02] p-4">
      <EditSection client={client} />
      <ProvisionSection client={client} />
      <KbIdentitySection client={client} />
      <VerifySection client={client} />
      <DangerSection client={client} />
      {client.provisionLog ? <ProvisionLogView client={client} /> : null}
    </div>
  );
}

function ProvisionSection({ client }: { client: AdminClient }): JSX.Element {
  const provision = useProvisionClient();
  const latch = useSubmitLatch();
  const [email, setEmail] = useState("");
  const done = client.status === "active";

  return (
    <section>
      <h3 className="text-sm font-semibold text-ink">2 · Abilita l&apos;accesso</h3>
      {done ? (
        <p className="mt-1 text-xs text-ink/70">
          Fatto. Organizzazione Auth0 <span className="font-mono">{client.orgId}</span>.
        </p>
      ) : (
        <p className="mt-1 text-xs text-ink/70">
          Crea l&apos;organizzazione su Auth0 e invia un invito al primo amministratore del cliente.
          Invia un&apos;email reale.
        </p>
      )}
      {!done ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // Guard the submit as well as the button: Enter in the field bypasses a
            // disabled button, and this action sends a real invitation email.
            if (!latch.claim()) return;
            provision.mutate(
              { clientId: client.id, adminEmail: email.trim() },
              { onSettled: latch.release },
            );
          }}
          className="mt-3 flex flex-wrap items-end gap-3"
        >
          <label className="block text-sm">
            <span className="font-medium text-ink">Email del primo amministratore</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="responsabile@revo.it"
              className="mt-1 w-72 rounded border border-line px-3 py-2 text-sm"
            />
          </label>
          <button
            type="submit"
            disabled={provision.isPending}
            className="rounded bg-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {provision.isPending ? "Configurazione…" : "Abilita e invita"}
          </button>
        </form>
      ) : null}
      {provision.isError ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {apiErrorMessage(provision.error)} — apri il registro sotto per vedere dove si è fermato.
        </p>
      ) : null}
      {provision.isSuccess ? (
        <ul className="mt-2 space-y-1 text-xs text-ink/70">
          {provision.data.steps.map((s) => (
            <li key={s.step}>
              {s.ok ? "✓" : "✕"} {s.step}
              {s.detail ? ` — ${s.detail}` : ""}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function KbIdentitySection({ client }: { client: AdminClient }): JSX.Element {
  const identity = useKbIdentity(client.orgId);
  const save = useSaveKbIdentity();
  const latch = useSubmitLatch();
  const [persona, setPersona] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);

  // Uncontrolled until the operator types: the saved value is the source of truth
  // until then, so a slow fetch cannot blank a field they were about to edit.
  const personaValue = persona ?? identity.data?.persona ?? "";
  const languageValue = language ?? identity.data?.language ?? "";

  if (!client.orgId) {
    return (
      <section>
        <h3 className="text-sm font-semibold text-ink">3 · Identità dell&apos;assistente</h3>
        <p className="mt-1 text-xs text-ink/70">
          Disponibile dopo il passaggio 2: l&apos;identità si applica all&apos;organizzazione del
          cliente, che non esiste ancora.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3 className="text-sm font-semibold text-ink">3 · Identità dell&apos;assistente</h3>
      <p className="mt-1 text-xs text-ink/70">
        Chi è l&apos;assistente della Knowledge Base per questo cliente. Se lo lasci vuoto viene
        usata una formulazione neutra:{" "}
        <span className="italic">{identity.data?.effectivePersona ?? "…"}</span>
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!latch.claim()) return;
          save.mutate(
            {
              clientId: client.id,
              orgId: client.orgId,
              persona: personaValue.trim() || null,
              language: languageValue.trim() || null,
            },
            { onSettled: latch.release },
          );
        }}
        className="mt-3 space-y-3"
      >
        <textarea
          value={personaValue}
          onChange={(e) => setPersona(e.target.value)}
          rows={3}
          maxLength={4000}
          placeholder="Sei l'assistente della knowledge base di Revo, impresa di costruzioni."
          className="w-full rounded border border-line px-3 py-2 text-sm"
        />
        <label className="block text-sm">
          <span className="font-medium text-ink">Lingua preferita</span>
          <input
            value={languageValue}
            onChange={(e) => setLanguage(e.target.value)}
            placeholder="Italiano"
            className="mt-1 w-48 rounded border border-line px-3 py-2 text-sm"
          />
          <span className="mt-1 block text-xs text-ink/60">
            Vuoto: risponde nella lingua della domanda.
          </span>
        </label>
        <button
          type="submit"
          disabled={save.isPending || identity.isLoading}
          className="rounded border border-line bg-white px-4 py-2 text-sm font-medium text-ink disabled:opacity-60"
        >
          {save.isPending ? "Salvataggio…" : "Salva identità"}
        </button>
      </form>
      {save.isError ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {apiErrorMessage(save.error)}
        </p>
      ) : null}
      {save.isSuccess ? <p className="mt-2 text-xs text-emerald-700">Salvata.</p> : null}
    </section>
  );
}

function VerifySection({ client }: { client: AdminClient }): JSX.Element {
  const verify = useVerifyClient();
  return (
    <section>
      <h3 className="text-sm font-semibold text-ink">Controlla la configurazione</h3>
      <p className="mt-1 text-xs text-ink/70">
        Interroga Auth0 e riporta com&apos;è configurato davvero — non quello che dice la nostra
        riga.
      </p>
      <button
        type="button"
        disabled={verify.isPending}
        onClick={() => verify.mutate(client.id)}
        className="mt-2 rounded border border-line bg-white px-4 py-2 text-sm font-medium text-ink disabled:opacity-60"
      >
        {verify.isPending ? "Controllo…" : "Controlla"}
      </button>
      {verify.isError ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {apiErrorMessage(verify.error)}
        </p>
      ) : null}
      {verify.isSuccess ? (
        <ul className="mt-3 space-y-1 text-xs">
          {verify.data.checks.map((c) => (
            <li key={c.check} className={c.ok ? "text-ink/70" : "text-red-600"}>
              {c.ok ? "✓" : "✕"}{" "}
              {/* Unknown check ids fall through to the raw id rather than being
                  hidden: a check the UI has not been taught about is still
                  information, and dropping it would make the list lie. */}
              {VERIFY_CHECK_LABEL[c.check] ?? c.check}
              {c.detail ? ` — ${c.detail}` : ""}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function EditSection({ client }: { client: AdminClient }): JSX.Element {
  const update = useUpdateClient(client.id);
  const latch = useSubmitLatch();
  const [displayName, setDisplayName] = useState<string | null>(null);

  // Null until the operator types, so the saved value stays the source of truth and a
  // refetch cannot overwrite a field mid-edit.
  const nameValue = displayName ?? client.displayName;

  return (
    <section>
      <h3 className="text-sm font-semibold text-ink">Anagrafica</h3>
      <p className="mt-1 text-xs text-ink/70">
        {/* Said out loud because the identificativo IS the Auth0 Organization name and
            cannot be renamed once it exists — an operator who does not know that will
            look for the field. */}
        Il nome si può correggere in qualsiasi momento. L&apos;identificativo{" "}
        <span className="font-mono">{client.slug}</span> no: è il nome dell&apos;organizzazione su
        Auth0.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!latch.claim()) return;
          update.mutate(
            {
              clientId: client.id,
              displayName: nameValue.trim(),
            },
            { onSettled: latch.release },
          );
        }}
        className="mt-3 space-y-3"
      >
        <label className="block text-sm">
          <span className="font-medium text-ink">Nome azienda</span>
          <input
            value={nameValue}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            className="mt-1 w-full rounded border border-line px-3 py-2 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={update.isPending}
          className="rounded border border-line bg-white px-4 py-2 text-sm font-medium text-ink disabled:opacity-60"
        >
          {update.isPending ? "Salvataggio…" : "Salva anagrafica"}
        </button>
      </form>
      {update.isError ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {apiErrorMessage(update.error)}
        </p>
      ) : null}
      {update.isSuccess ? <p className="mt-2 text-xs text-emerald-700">Salvata.</p> : null}
    </section>
  );
}

function DangerSection({ client }: { client: AdminClient }): JSX.Element {
  const remove = useDeleteClient();
  const [confirming, setConfirming] = useState(false);
  const deletable = client.status === "draft" && !client.orgId;

  return (
    <section className="border-t border-line pt-4">
      <div className="flex flex-wrap items-center gap-4">
        {deletable ? (
          confirming ? (
            <span className="flex items-center gap-2 text-sm">
              <span className="text-ink/70">Eliminare definitivamente?</span>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(client.id)}
                className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-60"
              >
                Elimina
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="text-xs text-ink/60 underline"
              >
                Annulla
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="text-sm text-red-600 underline hover:no-underline"
            >
              Elimina bozza
            </button>
          )
        ) : (
          // Stated rather than hidden: an operator who cannot find a delete button
          // assumes the UI is broken, whereas the real reason is a good one.
          <span className="text-xs text-ink/60">
            Non eliminabile: l&apos;organizzazione Auth0 esiste già e resterebbe orfana.
          </span>
        )}
      </div>
      {remove.isError ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {apiErrorMessage(remove.error)}
        </p>
      ) : null}
    </section>
  );
}

function ProvisionLogView({ client }: { client: AdminClient }): JSX.Element {
  const log = client.provisionLog;
  return (
    <section className="border-t border-line pt-4">
      <h3 className="text-sm font-semibold text-ink">Ultimo tentativo</h3>
      <p className="mt-1 text-xs text-ink/60">
        {log?.at ? new Date(log.at).toLocaleString("it-IT") : "—"}
        {log?.admin_email ? ` · invito a ${log.admin_email}` : ""}
      </p>
      {log?.error ? <p className="mt-1 text-xs text-red-600">{log.error}</p> : null}
      <ul className="mt-2 space-y-1 text-xs text-ink/70">
        {(log?.steps ?? []).map((s, i) => (
          <li key={`${s.step}-${i}`}>
            {s.ok ? "✓" : "✕"} {s.step}
            {s.detail ? ` — ${s.detail}` : ""}
          </li>
        ))}
      </ul>
    </section>
  );
}
